/** Brands CRUD (справочник для CMS-блока brand_strip). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Award,
  Loader2,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

interface Brand {
  id: number;
  slug: string;
  name: string;
  logo_url: string | null;
  description: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export function BrandsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | "new" | null>(null);

  const { data: brands, isLoading } = useQuery({
    queryKey: ["admin-brands"],
    queryFn: async () => {
      const { data } = await api.get("/brands");
      return data as Brand[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/brands/${id}`);
    },
    onSuccess: () => {
      toast.success("Бренд удалён");
      queryClient.invalidateQueries({ queryKey: ["admin-brands"] });
    },
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<number>({
    onConfirm: (id) => deleteMutation.mutateAsync(id),
    title: "Удалить бренд?",
  });

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/configuration">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Конфигурация
          </Link>
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading flex items-center gap-2">
            <Award className="w-6 h-6" />
            Бренды
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Справочник для CMS-блока brand_strip. Каждый бренд = логотип + slug.
          </p>
        </div>
        <Button onClick={() => setEditingId("new")}>
          <Plus className="w-4 h-4 mr-1" />
          Создать бренд
        </Button>
      </div>

      {editingId === "new" && (
        <BrandForm mode="create" onClose={() => setEditingId(null)} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {brands?.length === 0 && editingId !== "new" && (
          <Card className="lg:col-span-2">
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              Брендов нет. Создайте первый.
            </CardContent>
          </Card>
        )}
        {brands?.map((b) => (
          <Card key={b.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start gap-3">
                <div className="h-12 w-20 shrink-0 rounded border bg-muted/20 flex items-center justify-center overflow-hidden">
                  {b.logo_url ? (
                    <img
                      src={b.logo_url}
                      alt={b.name}
                      className="max-h-10 max-w-full object-contain"
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground">нет лого</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-base flex items-center gap-2">
                    {b.name}
                    {!b.is_active && (
                      <Badge variant="outline" className="text-[10px]">
                        disabled
                      </Badge>
                    )}
                  </CardTitle>
                  <div className="text-xs text-muted-foreground font-mono mt-0.5">
                    {b.slug}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {editingId === b.id ? (
                <BrandForm
                  mode="edit"
                  brand={b}
                  onClose={() => setEditingId(null)}
                />
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingId(b.id)}
                    >
                      Редактировать
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => askDelete(b.id, b.name)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      {deleteDialog}
    </div>
  );
}

function BrandForm({
  mode,
  brand,
  onClose,
}: {
  mode: "create" | "edit";
  brand?: Brand;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [slug, setSlug] = useState(brand?.slug ?? "");
  const [name, setName] = useState(brand?.name ?? "");
  const [description, setDescription] = useState(brand?.description ?? "");
  const [logoUrl, setLogoUrl] = useState(brand?.logo_url ?? "");
  const [isActive, setIsActive] = useState(brand?.is_active ?? true);
  const [sortOrder, setSortOrder] = useState(brand?.sort_order ?? 0);
  const [uploading, setUploading] = useState(false);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        slug: slug.trim(),
        name: name.trim(),
        description: description.trim() || null,
        logo_url: logoUrl.trim() || null,
        is_active: isActive,
        sort_order: sortOrder,
      };
      if (mode === "create") {
        const { data } = await api.post<Brand>("/brands", payload);
        return data;
      } else if (brand) {
        const { data } = await api.patch<Brand>(`/brands/${brand.id}`, payload);
        return data;
      }
      return null;
    },
    onSuccess: () => {
      toast.success(mode === "create" ? "Создан" : "Сохранён");
      queryClient.invalidateQueries({ queryKey: ["admin-brands"] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? "Ошибка"),
  });

  const onDrop = async (files: File[]) => {
    const file = files[0];
    if (!file || !brand) {
      if (!brand) {
        toast.info("Сначала сохраните бренд, затем загрузите логотип");
      }
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post<Brand>(`/brands/${brand.id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(data.logo_url ?? "");
      toast.success("Логотип загружен");
      queryClient.invalidateQueries({ queryKey: ["admin-brands"] });
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? "Ошибка загрузки");
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".png", ".jpg", ".jpeg", ".webp", ".svg"] },
    maxFiles: 1,
  });

  return (
    <Card className={mode === "edit" ? "border-primary/30" : undefined}>
      {mode === "create" && (
        <CardHeader>
          <CardTitle className="text-base">Новый бренд</CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-3 pt-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Slug *</Label>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="makita"
              className="font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Название *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Makita"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Описание</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Японский производитель электроинструмента"
          />
        </div>

        <div className="space-y-1.5">
          <Label>Логотип</Label>
          {brand && (
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded p-3 text-center cursor-pointer transition-colors text-xs ${
                isDragActive
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/30 hover:border-muted-foreground/50"
              }`}
            >
              <input {...getInputProps()} />
              {uploading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Загрузка…
                </div>
              ) : (
                <div className="flex items-center justify-center gap-1.5 text-muted-foreground">
                  <Upload className="w-3.5 h-3.5" />
                  {isDragActive ? "Отпусти файл" : "Загрузить логотип"}
                </div>
              )}
            </div>
          )}
          <Input
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://... или загрузите файл выше"
            className="font-mono text-xs"
          />
          {!brand && (
            <p className="text-[10px] text-muted-foreground">
              Сохраните бренд, потом откройте для загрузки логотипа.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Sort order</Label>
            <Input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
            />
          </div>
          <div className="flex items-center gap-2 pt-6">
            <Switch
              checked={isActive}
              onCheckedChange={setIsActive}
            />
            <Label>Активен</Label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!slug.trim() || !name.trim() || saveMutation.isPending}
          >
            {mode === "create" ? "Создать" : "Сохранить"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
